import { MusicalNote } from "../MusicalNote";
import { Instrument } from "./Instrument";

/**
 * Professional kick drum instrument with dual-layer synthesis using persistent oscillators.
 *
 * Creates a powerful kick drum sound using two synthesis layers:
 * - Sub-bass layer: Deep sine wave (40-60Hz) for low-end impact
 * - Punch layer: Frequency sweep (100-200Hz dropping) for body and thump
 *
 * Uses persistent oscillators for better performance and natural voice-stealing behavior.
 */
export class KickDrum extends Instrument {
  // Sub-bass layer parameters
  private subBassMix: number = 0.4;
  private subBassDecay: number = 0.2;

  // Punch layer parameters
  private punchStartFreq: number = 150;
  private punchEndFreq: number = 80;
  private punchMix: number = 0.5;
  private punchDecay: number = 0.15;
  private punchSweepTime: number = 0.05;

  // Global parameters
  private attackTime: number = 0.001;

  // Persistent audio nodes
  private subBassOsc: OscillatorNode; // Initialized in setupPersistentNodes
  private subBassGain: GainNode; // Initialized in setupPersistentNodes
  private punchOsc: OscillatorNode; // Initialized in setupPersistentNodes
  private punchGain: GainNode; // Initialized in setupPersistentNodes
  private masterEnvelope: GainNode; // Initialized in setupPersistentNodes

  constructor(audioContext: AudioContext, output: GainNode) {
    super(audioContext, output);

    // Set up persistent audio nodes that stay alive for the instrument's lifetime.
    // Master envelope controls overall kick output
    this.masterEnvelope = this.audioContext.createGain();
    this.masterEnvelope.gain.value = 0;
    this.masterEnvelope.connect(this.gain);

    // Layer 1: Sub-bass oscillator (40-60Hz)
    this.subBassOsc = this.audioContext.createOscillator();
    this.subBassOsc.type = "sine";
    this.subBassGain = this.audioContext.createGain();
    this.subBassGain.gain.value = this.subBassMix;
    this.subBassOsc.connect(this.subBassGain);
    this.subBassGain.connect(this.masterEnvelope);
    this.subBassOsc.start();

    // Layer 2: Punch oscillator with frequency sweep (100-200Hz dropping)
    this.punchOsc = this.audioContext.createOscillator();
    this.punchOsc.type = "sine";
    this.punchOsc.frequency.value = this.punchStartFreq;
    this.punchGain = this.audioContext.createGain();
    this.punchGain.gain.value = this.punchMix;
    this.punchOsc.connect(this.punchGain);
    this.punchGain.connect(this.masterEnvelope);
    this.punchOsc.start();

    this.initializeParameters();
  }

  /**
   * Play a kick drum note using persistent oscillators with click-free voice stealing.
   */
  playNote(note: MusicalNote, preStartTime: number, _duration: number): void {
    const velocity = note.velocity ?? 1.0;

    // Cancel previous envelope and hold current value to avoid clicks
    this.masterEnvelope.gain.cancelAndHoldAtTime(preStartTime);
    this.punchGain.gain.cancelAndHoldAtTime(preStartTime);
    this.punchOsc.frequency.cancelAndHoldAtTime(preStartTime);
    this.subBassGain.gain.cancelAndHoldAtTime(preStartTime);

    // Use a small offset to avoid clicks
    const startTime = preStartTime + 0.02;
    const attackEnd = startTime + this.attackTime;

    // Sub Bass Envelope
    this.subBassGain.gain.linearRampToValueAtTime(this.subBassMix, startTime);
    this.subBassGain.gain.setTargetAtTime(0, startTime, this.subBassDecay);

    // Punch envelope
    this.punchGain.gain.linearRampToValueAtTime(this.punchMix, startTime);
    this.punchGain.gain.setTargetAtTime(0, startTime, this.punchDecay);

    // Punch frequency sweep
    this.punchOsc.frequency.linearRampToValueAtTime(
      this.punchStartFreq,
      startTime,
    );
    this.punchOsc.frequency.exponentialRampToValueAtTime(
      this.punchEndFreq,
      startTime + this.punchSweepTime,
    );

    // Master envelop
    this.masterEnvelope.gain.setTargetAtTime(
      velocity,
      preStartTime,
      this.attackTime,
    );

    // Ramp down for whatever reason
    // const longestDecay = Math.max(this.subBassDecay, this.punchDecay);
    // this.masterEnvelope.gain.setTargetAtTime(0, startTime + longestDecay, 0.1);
  }

  /**
   * Get valid MIDI notes for kick drum.
   */
  getValidNotes(): number[] {
    return [36]; // Only C2 kick drum note
  }

  /**
   * Clean up persistent nodes when instrument is destroyed.
   */
  onDestroy(): void {
    // Stop and disconnect persistent oscillators
    this.subBassOsc.stop();
    this.subBassOsc.disconnect();
    this.subBassGain.disconnect();

    this.punchOsc.stop();
    this.punchOsc.disconnect();
    this.punchGain.disconnect();

    this.masterEnvelope.disconnect();

    super.onDestroy();
  }

  /**
   * Initialize kick drum parameters for all three layers.
   */
  private initializeParameters(): void {
    // Sub-bass layer parameters
    this.addParameter(
      () => this.subBassOsc.frequency.value,
      (value) => {
        this.subBassOsc.frequency.value = value;
      },
      {
        key: "subBassFreq",
        label: "Sub-Bass Freq",
        min: 30,
        max: 300,
        defaultValue: 50,
        step: 1,
        unit: "Hz",
        category: "Kick",
      },
    );

    this.addParameter(
      () => this.subBassMix,
      (value) => {
        this.subBassMix = value;
      },
      {
        key: "subBassGain",
        label: "Sub-Bass Gain",
        min: 0,
        max: 1.0,
        defaultValue: 0.4,
        step: 0.05,
        category: "Kick",
      },
    );

    this.addParameter(
      () => this.subBassDecay,
      (value) => {
        this.subBassDecay = value;
      },
      {
        key: "subBassDecay",
        label: "Sub-Bass Decay",
        min: 0.0,
        max: 0.5,
        defaultValue: 0.1,
        step: 0.01,
        unit: "s",
        category: "Kick",
      },
    );

    // Punch layer parameters
    this.addParameter(
      () => this.punchStartFreq,
      (value) => {
        this.punchStartFreq = value;
      },
      {
        key: "punchStartFreq",
        label: "Punch Start Freq",
        min: 50,
        max: 300,
        defaultValue: 160,
        step: 5,
        unit: "Hz",
        category: "Kick",
      },
    );

    this.addParameter(
      () => this.punchEndFreq,
      (value) => {
        this.punchEndFreq = value;
      },
      {
        key: "punchEndFreq",
        label: "Punch End Freq",
        min: 30,
        max: 200,
        defaultValue: 60,
        step: 5,
        unit: "Hz",
        category: "Kick",
      },
    );

    this.addParameter(
      () => this.punchMix,
      (value) => {
        this.punchMix = value;
      },
      {
        key: "punchMix",
        label: "Punch Mix",
        min: 0,
        max: 1.0,
        defaultValue: 0.5,
        step: 0.05,
        category: "Kick",
      },
    );

    this.addParameter(
      () => this.punchDecay,
      (value) => {
        this.punchDecay = value;
      },
      {
        key: "punchDecay",
        label: "Punch Decay",
        min: 0.0,
        max: 0.5,
        defaultValue: 0.05,
        step: 0.002,
        unit: "s",
        category: "Kick",
      },
    );

    this.addParameter(
      () => this.punchSweepTime,
      (value) => {
        this.punchSweepTime = value;
      },
      {
        key: "punchSweepTime",
        label: "Punch Sweep Time",
        min: 0.01,
        max: 0.2,
        defaultValue: 0.05,
        step: 0.01,
        unit: "s",
        category: "Kick",
      },
    );

    // Global parameters
    this.addParameter(
      () => this.attackTime,
      (value) => {
        this.attackTime = value;
      },
      {
        key: "attackTime",
        label: "Attack Time",
        min: 0.0001,
        max: 0.01,
        defaultValue: 0.001,
        step: 0.0001,
        unit: "s",
        category: "Kick",
      },
    );
  }
}
